import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import {
  BarChart3, Table2, Download, ArrowLeft, Loader2, FileDown, Presentation,
  TrendingUp, DollarSign, Shield, Clock, Target, AlertCircle, Check, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHeroCompact } from '../components/layout/PageHeroCompact';
import { getLightSectionTheme } from '../lib/sectionThemes';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

export default function ProspectusComparePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [prospectuses, setProspectuses] = useState([]);
  const [allProspectuses, setAllProspectuses] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [viewMode, setViewMode] = useState('both'); // 'table', 'charts', 'both'
  const [error, setError] = useState(null);

  // Get IDs from URL params
  useEffect(() => {
    const ids = searchParams.get('ids');
    if (ids) {
      setSelectedIds(ids.split(','));
    }
  }, [searchParams]);

  // Fetch all prospectuses for selection
  useEffect(() => {
    fetchAllProspectuses();
  }, []);

  const fetchAllProspectuses = async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const response = await api.get(`/prospectus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllProspectuses(response.data);
    } catch (err) {
      logger.error('Error fetching prospectuses:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSelectedProspectuses = useCallback(async () => {
    setLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const responses = await Promise.all(
        selectedIds.map(id => 
          api.get(`/prospectus/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      setProspectuses(responses.map(r => r.data));
    } catch (err) {
      logger.error('Error fetching prospectuses:', err);
      setError('Failed to load prospectuses for comparison');
    } finally {
      setLoading(false);
    }
  }, [selectedIds]);

  // Fetch selected prospectuses for comparison
  useEffect(() => {
    if (selectedIds.length > 0) {
      fetchSelectedProspectuses();
    }
  }, [selectedIds, fetchSelectedProspectuses]);

  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      }
      if (prev.length >= 4) {
        setError('Maximum 4 scenarios can be compared');
        return prev;
      }
      return [...prev, id];
    });
  };

  const exportComparison = async (format) => {
    if (selectedIds.length < 2) {
      setError('Select at least 2 prospectuses to compare');
      return;
    }
    
    setExporting(format);
    try {
      const token = secureStorage.getItem('session_token');
      const response = await api.post(
        `/prospectus/compare/export/${format}`,
        { prospectus_ids: selectedIds },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'pdf' ? 'pdf' : 'pptx';
      link.setAttribute('download', `Investment_Comparison.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    const symbols = { USD: '$', EUR: '€', GBP: '£' };
    const symbol = symbols[currency] || '$';
    if (!amount) return `${symbol}0`;
    if (amount >= 1000000) return `${symbol}${(amount/1000000).toFixed(1)}M`;
    if (amount >= 1000) return `${symbol}${(amount/1000).toFixed(0)}K`;
    return `${symbol}${amount.toLocaleString()}`;
  };

  const getConfidenceBadge = (level) => {
    const styles = {
      high: 'bg-emerald-100 text-emerald-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-rose-100 text-rose-800'
    };
    return <Badge className={styles[level] || styles.medium}>{level || 'N/A'}</Badge>;
  };

  // Prepare data for charts
  const roiChartData = prospectuses.map((p, i) => ({
    name: p.project_name?.substring(0, 15) || `Scenario ${i+1}`,
    ROI: (p.roi_projection || 0) / 100,
    'Best Case': (p.scenario_analysis?.best_case_roi || 0) / 100,
    'Worst Case': (p.scenario_analysis?.worst_case_roi || 0) / 100,
    fill: COLORS[i % COLORS.length]
  }));

  const investmentChartData = prospectuses.map((p, i) => ({
    name: p.project_name?.substring(0, 15) || `Scenario ${i+1}`,
    Investment: p.total_investment || 0,
    'Financial Impact': p.annual_financial_impact || 0,
    'Net Benefit': p.net_financial_impact || 0
  }));

  const radarData = prospectuses.length > 0 ? [
    { metric: 'ROI', fullMark: 5, ...Object.fromEntries(prospectuses.map((p, i) => [`S${i+1}`, Math.min((p.roi_projection || 0) / 100, 5)])) },
    { metric: 'Confidence', fullMark: 100, ...Object.fromEntries(prospectuses.map((p, i) => [`S${i+1}`, p.monte_carlo_results?.prob_positive_roi || 50])) },
    { metric: 'Payback Speed', fullMark: 100, ...Object.fromEntries(prospectuses.map((p, i) => [`S${i+1}`, Math.max(0, 100 - (p.payback_period_months || 12) * 5)])) },
    { metric: 'Impact Scale', fullMark: 100, ...Object.fromEntries(prospectuses.map((p, i) => [`S${i+1}`, Math.min((p.annual_financial_impact || 0) / 10000, 100)])) },
    { metric: 'Risk Profile', fullMark: 100, ...Object.fromEntries(prospectuses.map((p, i) => [`S${i+1}`, 100 - Math.abs((p.scenario_analysis?.worst_case_roi || 0) / 100)])) }
  ] : [];

  // Selection mode (no prospectuses selected yet)
  if (selectedIds.length === 0 && !loading) {
    const __pageThemeSelect = getLightSectionTheme('Navy');
    return (
      <PageShell theme={__pageThemeSelect} hero={(
        <PageHeroCompact title={`Compare Prospectuses`} description={`Diff two prospectuses side by side.`} theme={__pageThemeSelect} />
      )}>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="outline" onClick={() => navigate('/prospectus')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
                      </div>

          {allProspectuses.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No Prospectuses Found</h3>
                <p className="text-gray-500 mt-2">Create at least 2 business cases to compare them.</p>
                <Button onClick={() => navigate('/prospectus/new')} className="mt-4">
                  Create Prospectus
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allProspectuses.map(p => (
                <Card 
                  key={p.prospectus_id}
                  className={`cursor-pointer transition-all ${
                    selectedIds.includes(p.prospectus_id) 
                      ? 'ring-2 ring-blue-500 bg-blue-50' 
                      : 'hover:shadow-md'
                  }`}
                  onClick={() => toggleSelection(p.prospectus_id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold truncate">{p.project_name || 'Untitled'}</h3>
                        <p className="text-sm text-gray-500 truncate">{p.organization_name}</p>
                      </div>
                      <Checkbox 
                        checked={selectedIds.includes(p.prospectus_id)}
                        className="mt-1"
                      />
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Investment:</span>
                        <p className="font-medium">{formatCurrency(p.total_investment, p.currency)}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">ROI:</span>
                        <p className="font-medium text-emerald-600">{((p.roi_projection || 0) / 100).toFixed(2)}x</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {selectedIds.length > 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white shadow-lg rounded-full px-6 py-3 flex items-center gap-4 border">
              <span className="text-sm font-medium">{selectedIds.length} selected</span>
              <Button onClick={() => setSelectedIds([])}>
                Clear
              </Button>
              <Button 
                onClick={() => navigate(`/prospectus/compare?ids=${selectedIds.join(',')}`)}
                disabled={selectedIds.length < 2}
              >
                Compare Selected
              </Button>
            </div>
          )}
        </div>
      </div>
      </PageShell>
    );
  }

  if (loading) {
    const __pageThemeLoading = getLightSectionTheme('Navy');
    return (
      <PageShell theme={__pageThemeLoading} hero={(
        <PageHeroCompact title={`Compare Prospectuses`} description={`Diff two prospectuses side by side.`} theme={__pageThemeLoading} />
      )}>
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
      </PageShell>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHeroCompact title={`Compare Investment Scenarios`} description={`Diff two prospectuses side by side.`} theme={__pageTheme} />
    )}>
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/prospectus')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold">Scenario Comparison</h1>
                <p className="text-sm text-gray-500">{prospectuses.length} scenarios compared</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* View Mode Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'table' ? 'bg-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Table2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('charts')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'charts' ? 'bg-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('both')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'both' ? 'bg-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Both
                </button>
              </div>
              
              {/* Export Buttons */}
              <Button 
                variant="outline" 
                onClick={() => exportComparison('pdf')}
                disabled={exporting === 'pdf'}
              >
                {exporting === 'pdf' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4 mr-2" />
                )}
                PDF
              </Button>
              <Button 
                variant="outline"
                onClick={() => exportComparison('pptx')}
                disabled={exporting === 'pptx'}
              >
                {exporting === 'pptx' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Presentation className="w-4 h-4 mr-2" />
                )}
                PowerPoint
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Scenario Headers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {prospectuses.map((p, i) => (
            <Card key={p.prospectus_id} className="border-t-4" style={{ borderTopColor: COLORS[i % COLORS.length] }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[i % COLORS.length] }}
                  />
                  <span className="text-sm font-medium text-gray-500">Scenario {i + 1}</span>
                </div>
                <h3 className="font-semibold truncate">{p.project_name || 'Untitled'}</h3>
                <p className="text-sm text-gray-500 truncate">{p.organization_name}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Table View */}
        {(viewMode === 'table' || viewMode === 'both') && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Table2 className="w-5 h-5" />
                Comparison Table
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Metric</th>
                      {prospectuses.map((p, i) => (
                        <th key={p.prospectus_id} className="text-right py-3 px-4 font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <div 
                              className="w-2 h-2 rounded-full" 
                              style={{ backgroundColor: COLORS[i % COLORS.length] }}
                            />
                            <span className="truncate max-w-[120px]">{p.project_name || `Scenario ${i+1}`}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="py-3 px-4 text-gray-600">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" /> Total Investment
                        </div>
                      </td>
                      {prospectuses.map(p => (
                        <td key={p.prospectus_id} className="py-3 px-4 text-right font-medium">
                          {formatCurrency(p.total_investment, p.currency)}
                        </td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="py-3 px-4 text-gray-600">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" /> ROI Projection
                        </div>
                      </td>
                      {prospectuses.map(p => (
                        <td key={p.prospectus_id} className="py-3 px-4 text-right font-bold text-emerald-600">
                          {((p.roi_projection || 0) / 100).toFixed(2)}x
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-gray-600">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" /> Annual Financial Impact
                        </div>
                      </td>
                      {prospectuses.map(p => (
                        <td key={p.prospectus_id} className="py-3 px-4 text-right font-medium">
                          {formatCurrency(p.annual_financial_impact, p.currency)}
                        </td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="py-3 px-4 text-gray-600">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" /> Net Financial Impact
                        </div>
                      </td>
                      {prospectuses.map(p => (
                        <td key={p.prospectus_id} className="py-3 px-4 text-right font-medium">
                          {formatCurrency(p.net_financial_impact, p.currency)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-gray-600">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" /> Payback Period
                        </div>
                      </td>
                      {prospectuses.map(p => (
                        <td key={p.prospectus_id} className="py-3 px-4 text-right font-medium">
                          {(p.payback_period_months || 0).toFixed(1)} months
                        </td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="py-3 px-4 text-gray-600">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4" /> Confidence Level
                        </div>
                      </td>
                      {prospectuses.map(p => (
                        <td key={p.prospectus_id} className="py-3 px-4 text-right">
                          {getConfidenceBadge(p.confidence_level)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-gray-600">
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4" /> Prob. Positive ROI
                        </div>
                      </td>
                      {prospectuses.map(p => (
                        <td key={p.prospectus_id} className="py-3 px-4 text-right font-medium">
                          {(p.monte_carlo_results?.prob_positive_roi || 0).toFixed(0)}%
                        </td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="py-3 px-4 text-gray-600">Best Case ROI</td>
                      {prospectuses.map(p => (
                        <td key={p.prospectus_id} className="py-3 px-4 text-right font-medium text-emerald-600">
                          {((p.scenario_analysis?.best_case_roi || 0) / 100).toFixed(2)}x
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-gray-600">Worst Case ROI</td>
                      {prospectuses.map(p => (
                        <td key={p.prospectus_id} className="py-3 px-4 text-right font-medium text-rose-600">
                          {((p.scenario_analysis?.worst_case_roi || 0) / 100).toFixed(2)}x
                        </td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="py-3 px-4 text-gray-600">
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4" /> Target KPIs
                        </div>
                      </td>
                      {prospectuses.map(p => (
                        <td key={p.prospectus_id} className="py-3 px-4 text-right font-medium">
                          {p.target_kpis?.length || 0}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-gray-600">Interventions</td>
                      {prospectuses.map(p => (
                        <td key={p.prospectus_id} className="py-3 px-4 text-right font-medium">
                          {p.interventions?.length || 0}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Charts View */}
        {(viewMode === 'charts' || viewMode === 'both') && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ROI Comparison Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">ROI Comparison</CardTitle>
                <CardDescription>Expected, Best Case, and Worst Case ROI</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={roiChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis unit="x" />
                    <Tooltip formatter={(value) => `${value.toFixed(2)}x`} />
                    <Legend />
                    <Bar dataKey="ROI" fill="#3B82F6" name="Expected ROI" />
                    <Bar dataKey="Best Case" fill="#10B981" name="Best Case" />
                    <Bar dataKey="Worst Case" fill="#EF4444" name="Worst Case" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Investment vs Impact Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Investment vs Impact</CardTitle>
                <CardDescription>Financial comparison across scenarios</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={investmentChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => value >= 1000000 ? `${(value/1000000).toFixed(0)}M` : value >= 1000 ? `${(value/1000).toFixed(0)}K` : value} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="Investment" fill="#F59E0B" name="Investment" />
                    <Bar dataKey="Financial Impact" fill="#10B981" name="Annual Impact" />
                    <Bar dataKey="Net Benefit" fill="#3B82F6" name="Net Benefit" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Radar Chart for Multi-dimensional Comparison */}
            {radarData.length > 0 && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-base">Multi-Dimensional Comparison</CardTitle>
                  <CardDescription>Radar view of key performance indicators</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="metric" />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} />
                      {prospectuses.map((p, i) => (
                        <Radar
                          key={p.prospectus_id}
                          name={p.project_name?.substring(0, 15) || `Scenario ${i+1}`}
                          dataKey={`S${i+1}`}
                          stroke={COLORS[i % COLORS.length]}
                          fill={COLORS[i % COLORS.length]}
                          fillOpacity={0.3}
                        />
                      ))}
                      <Legend />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Winner Summary */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Recommendation Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Highest ROI</p>
                <p className="font-semibold text-lg">
                  {prospectuses.reduce((best, p) => 
                    (p.roi_projection || 0) > (best?.roi_projection || 0) ? p : best
                  , null)?.project_name || 'N/A'}
                </p>
                <p className="text-emerald-600 font-bold">
                  {(Math.max(...prospectuses.map(p => p.roi_projection || 0)) / 100).toFixed(2)}x ROI
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Lowest Risk</p>
                <p className="font-semibold text-lg">
                  {prospectuses.reduce((best, p) => 
                    (p.monte_carlo_results?.prob_positive_roi || 0) > (best?.monte_carlo_results?.prob_positive_roi || 0) ? p : best
                  , null)?.project_name || 'N/A'}
                </p>
                <p className="text-blue-600 font-bold">
                  {Math.max(...prospectuses.map(p => p.monte_carlo_results?.prob_positive_roi || 0)).toFixed(0)}% Prob. Positive
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Fastest Payback</p>
                <p className="font-semibold text-lg">
                  {prospectuses.reduce((best, p) => 
                    (p.payback_period_months || 999) < (best?.payback_period_months || 999) ? p : best
                  , null)?.project_name || 'N/A'}
                </p>
                <p className="text-purple-600 font-bold">
                  {Math.min(...prospectuses.map(p => p.payback_period_months || 999)).toFixed(1)} months
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-rose-100 border border-rose-200 text-rose-800 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-2 text-rose-600 hover:text-rose-800">×</button>
        </div>
      )}
    </div>
    </PageShell>
  );
}
